<template>
  <div qaid="paint-scheme-swatch-groups">
    <fieldset>
      <legend class="sr-only">{{ optionCategory }}</legend>
      <div v-for="swatchCategory in swatchCategories" :key="`${groupId}-${swatchCategory.category}`">
        <template v-if="swatchCategory.colorChoices?.length">
          <h4 v-if="swatchCategory.category !== uncategorized" class="mt-2 text-xs font-normal">
            {{ swatchCategory.label }}
          </h4>
          <div class="mt-1 w-full grid gap-2" :class="swatchWrapperClasses" :aria-label="swatchCategory.label">
            <label
              v-for="colorChoice in swatchCategory.colorChoices"
              :key="colorChoice.id"
              v-tippy="colorChoice.tooltip"
              v-bind="colorChoice.additionalAttributes"
              :qaid="`color-swatch-label-${colorChoice.id}`"
              class="block cursor-pointer relative"
            >
              <span class="sr-only">{{ colorChoice.description }}</span>
              <input
                :id="`${colorChoice.name}-swatch-${colorChoice.id}`"
                type="radio"
                class="appearance-none absolute inset-0 w-full h-full background-transparent rounded-none"
                :name="`swatch-options-${groupId}`"
                :value="colorChoice"
                :checked="activeSwatch && activeSwatch.id === colorChoice.id"
                @change="onSwatchSelected(colorChoice)"
              />
              <figure class="w-full" :class="swatchClasses" aria-hidden="true">
                <img :src="colorChoice.imageUrl" alt="" class="w-full h-full object-cover" loading="lazy" />
              </figure>
            </label>
          </div>
        </template>
      </div>
    </fieldset>
    <template v-if="tooltipColorChoices && tooltipColorChoices.length">
      <customization-swatch-tooltip v-for="color in tooltipColorChoices" v-cloak :key="color.id" :color="color" />
    </template>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import CustomizationSwatchTooltip from '@/components/containers/buy-zone/customization/CustomizationSwatchTooltip.vue';
import {Cloudinary, CloudinaryConstants} from '@/utils/image/cloudinary-helpers';

const UNCATEGORIZED = 'UNCATEGORIZED';
const colorOptionCategories = ['Body Color', 'Logo Color'];

export default {
  name: 'PaintSchemeSwatchGroups',
  components: {CustomizationSwatchTooltip},
  props: {
    selectedOption: {
      type: Object,
      required: true,
    },
    groupId: {
      type: Number,
      required: true,
    },
    topTierId: {
      type: Number,
      required: true,
    },
    optionCategory: {
      type: String,
      default: '',
    },
    colorChoices: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      uncategorized: UNCATEGORIZED,
      swatchCategories: {
        UNCATEGORIZED: {
          colorChoices: [],
          category: UNCATEGORIZED,
        },
      },
      colorCodes: [UNCATEGORIZED],
    };
  },
  computed: {
    ...mapGetters('bikeCustomization', ['colorCategories']),
    ...mapGetters('viewSize', ['viewBreakpointMoreThan']),

    swatchGroupOptions() {
      return this.colorChoices?.map((color) => this.convertToSwatchOption(color));
    },

    swatchWrapperClasses() {
      return colorOptionCategories.includes(this.optionCategory) ? 'grid-cols-3' : 'grid-cols-5';
    },
    swatchClasses() {
      return colorOptionCategories.includes(this.optionCategory) ? 'aspect-[3]' : 'aspect-square';
    },

    tooltipColorChoices() {
      return this.viewBreakpointMoreThan('medium') ? this.colorChoices : null;
    },

    activeSwatch() {
      if (!this.selectedOption) return null;
      for (const category of Object.values(this.swatchCategories)) {
        const activeItem = category.colorChoices.find((choice) => choice.id === this.selectedOption.id);
        if (activeItem) return activeItem;
      }
      return null;
    },
  },
  mounted() {
    this.generateSwatchCategories();
    this.categorizeColorChoices();
  },
  methods: {
    ...mapActions('bikeCustomization', ['updateSelection']),

    generateSwatchCategories() {
      this.colorCategories?.forEach((category) => {
        this.swatchCategories = {
          ...this.swatchCategories,
          [category.code]: {
            colorChoices: [],
            category: category.code,
            label: category.name,
          },
        };
        this.colorCodes.push(category.code);
      });
    },
    categorizeColorChoices() {
      this.swatchGroupOptions?.forEach((color) => {
        if (color.category === '') {
          this.swatchCategories[this.uncategorized].colorChoices.push(color);
          return;
        }
        if (this.colorCodes.includes(color.category)) {
          this.swatchCategories[color.category].colorChoices.push(color);
        }
      });
    },

    getRadioName(category) {
      if (category !== this.uncategorized) {
        return `swatch-options-${this.groupId}-${category}`;
      } else {
        return `swatch-options-${this.groupId}-${this.optionCategory.id}`;
      }
    },

    convertToSwatchOption(color) {
      if (!color) return null;
      const attributes = {};

      if (this.tooltipColorChoices?.length) {
        attributes.tooltip = {
          theme: 'customization-swatch-tooltip',
          content: () => this.getTooltipContent(color),
          placement: 'top',
          interactive: false,
          allowHtml: true,
        };

        attributes.additionalAttributes = {
          qaid: `customization-tooltip-trigger-${color.id}`,
        };
      }
      attributes.category = color.colorCategory ?? '';

      const isWide = this.swatchClasses === 'aspect-[3]';
      const width = isWide ? 141 : 80;
      const height = isWide ? 47 : 80;
      const dpr = window.devicePixelRatio || 1;

      const imageTransformations = {
        w: width,
        h: height,
        dpr: dpr,
        ...CloudinaryConstants?.DEFAULT_PARAMETERS,
      };

      const imageUrl = Cloudinary.createAssetUrl(color.imageThumbnailReference, imageTransformations);

      return {
        id: color.id,
        groupId: this.groupId,
        topTierPackageId: this.topTierId,
        description: color.description,
        name: color.description?.replace(/\W/g, ''),
        imageUrl,
        ...attributes,
      };
    },

    async onSwatchSelected(newSwatch) {
      if (this.activeSwatch && this.activeSwatch.id === newSwatch.id) return;
      try {
        await this.updateSelection({
          selections: [
            {groupId: newSwatch.groupId, choiceId: newSwatch.id, topTierPackageId: newSwatch.topTierPackageId},
          ],
          module: 'paint-scheme',
        });
        this.$emit('color-changed', {...newSwatch, radioName: this.getRadioName(this.optionCategory)});
      } catch (error) {
        console.error('Error updating selection:', error);
      }
    },

    getTooltipContent(color) {
      let content = document.getElementById(`customization-swatch-tooltip-${color.id}`)?.cloneNode(true);
      if (content) {
        content.style.display = 'block';
      }
      return content;
    },
  },
};
</script>
<style lang="scss" scoped>
label:has(input:checked) {
  --swatch-active-outline-color: currentColor;
}

figure {
  transition: outline-color 0.2s ease;
  outline: 2px solid var(--swatch-active-outline-color, transparent);
  outline-offset: 2px;
}

input:focus-visible {
  outline: 3px solid var(--blue-100);
  outline-offset: 4px;
}
</style>
